/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */

import { useUser, useAuth as useClerkAuth } from '@clerk/clerk-react';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { getDemoUserFromStorage, DemoUser } from '../utils/demoMode';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://localhost:7095';

export interface UserProfile {
  id: string;
  email: string;
  displayName: string | null;
  subscriptionTier: 'free' | 'pro' | 'premium';
  features: {
    allowedKeys?: string[] | 'all';
    allowedScales?: string[] | 'all';
    allowedChords?: string[] | 'all';
    jamTracks?: number;
    harmonicCompanionConversationsPerMonth?: number | 'unlimited';
    currentHarmonicCompanionMessages?: number;
    practiceMode?: string;
    creatorContent?: string;
    offlineMode?: boolean;
    customTunings?: boolean;
    progressTracking?: string;
  };
  trialEndsAt: string | null;
  currentPeriodEnd: string | null;
  createdAt: string;
  lastActiveAt: string | null;
}

export const useAuth = () => {
  const { isSignedIn, user, isLoaded: isClerkLoaded } = useUser();
  const { getToken } = useClerkAuth();
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [demoUser, setDemoUser] = useState<DemoUser | null>(null);

  useEffect(() => {
    if (!isClerkLoaded) {
      return;
    }

    // Check for demo mode and initialize if needed
    const initDemo = async () => {
      // Try to get from storage first (fast path for refreshes)
      let demo = getDemoUserFromStorage();

      // If not in storage, try to initialize from URL token
      if (!demo) {
        const { initializeDemoMode } = await import('../utils/demoMode');
        const result = await initializeDemoMode();
        if (result.success && result.user) {
          demo = result.user;
        }
      }

      if (demo) {
        setDemoUser(demo);
        // Create a user profile from demo user
        setUserProfile({
          id: demo.userId,
          email: demo.email,
          displayName: demo.demoLabel,
          subscriptionTier: 'pro',
          features: {
            allowedKeys: 'all',
            allowedScales: 'all',
            allowedChords: 'all',
            jamTracks: 999,
            harmonicCompanionConversationsPerMonth: 'unlimited',
            currentHarmonicCompanionMessages: 0,
            practiceMode: 'advanced',
            creatorContent: 'all',
            offlineMode: true,
            customTunings: true,
            progressTracking: 'detailed'
          },
          trialEndsAt: null,
          currentPeriodEnd: demo.demoExpiresAt,
          createdAt: new Date().toISOString(),
          lastActiveAt: new Date().toISOString()
        });
        setIsLoading(false);
        return;
      }

      if (!isSignedIn) {
        setUserProfile(null);
        setDemoUser(null);
        setIsLoading(false);
        return;
      }

      // Fetch user profile from backend
      const fetchUserProfile = async () => {
        try {
          const token = await getToken();
          const response = await axios.get(`${API_BASE_URL}/api/account/me`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
          setUserProfile(response.data);
          setError(null);
        } catch (err) {
          console.error('Error fetching user profile:', err);
          setError('Failed to load user profile');
        } finally {
          setIsLoading(false);
        }
      };

      await fetchUserProfile();
    };

    initDemo();
  }, [isSignedIn, isClerkLoaded, getToken]);

  /**
   * Get auth token for API calls
   */
  const getAuthToken = async (): Promise<string | null> => {
    if (!isSignedIn) return null;
    return await getToken();
  };

  /**
   * Check if user has access to a specific feature
   */
  const hasFeatureAccess = (featureKey: string, requiredValue?: any): boolean => {
    if (!userProfile) return false;

    const featureValue = userProfile.features[featureKey as keyof typeof userProfile.features];

    if (requiredValue !== undefined) {
      return featureValue === requiredValue;
    }

    return !!featureValue;
  };

  /**
   * Check if user can select a specific key
   */
  const canSelectKey = (key: string): boolean => {
    if (!userProfile) return false;

    const allowedKeys = userProfile.features.allowedKeys;

    if (allowedKeys === 'all') return true;
    if (Array.isArray(allowedKeys)) return allowedKeys.includes(key);

    return false;
  };

  /**
   * Check if user has reached conversation limit
   */
  const hasReachedChatLimit = (): boolean => {
    if (!userProfile) return true;

    const limit = userProfile.features.harmonicCompanionConversationsPerMonth;
    const current = userProfile.features.currentHarmonicCompanionMessages || 0;

    if (limit === 'unlimited') return false;
    if (typeof limit === 'number') return current >= limit;

    return true;
  };

  const isInDemoMode = !!demoUser;

  return {
    // Clerk user data
    clerkUser: user,
    isSignedIn: isSignedIn || isInDemoMode,
    isLoading,
    error,

    // Backend user profile
    userProfile,
    subscriptionTier: userProfile?.subscriptionTier || 'free',

    // Helper functions
    getAuthToken,
    hasFeatureAccess,
    canSelectKey,
    hasReachedChatLimit,

    // Feature flags (convenience)
    isPro: userProfile?.subscriptionTier === 'pro' || userProfile?.subscriptionTier === 'premium' || isInDemoMode,
    isPremium: userProfile?.subscriptionTier === 'premium',
    isFree: (userProfile?.subscriptionTier === 'free' || !userProfile) && !isInDemoMode,
    isAnonymous: !isSignedIn && !isLoading && !isInDemoMode,
    isDemoUser: isInDemoMode,  // Renamed from isDemo for clarity
    isDemo: isInDemoMode,      // Keep for backward compatibility during transition
    demoUser
  };
};
